// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analyticsMessages } from "../../messages";
import { EmptyState } from "../EmptyState";
import { seriesColors, surfaceCardStyle } from "./tokens";

interface ReferenceLineDef {
  y: number;
  color: string;
  strokeDasharray?: string;
  label?: string;
}

interface BarChartTileV2Props<T extends Record<string, any> = Record<string, any>> {
  title: string;
  description?: string;
  data: T[];
  labelKey: keyof T & string;
  valueKey: keyof T & string;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
  referenceLines?: ReferenceLineDef[];
  labelMaxChars?: number;
  height?: number;
  color?: string;
  colorRamp?: boolean;
  showValueLabels?: boolean;
  valueFormatter?: (v: number) => string;
  horizontal?: boolean;
}

const CustomTooltip: React.FC<any> = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || !payload.length) return null;

  const v = payload[0].value;

  return (
    <Box
      padding={3}
      borderRadius={3}
      style={{
        background: "rgba(17, 24, 39, 0.95)",
        color: "#fff",
        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        fontSize: 12,
        minWidth: 120,
      }}
    >
      <Box style={{ opacity: 0.8, marginBottom: 4 }}>{label}</Box>
      <Box style={{ fontWeight: 600 }}>{valueFormatter ? valueFormatter(v) : v}</Box>
    </Box>
  );
};

export const BarChartTile: React.FC<BarChartTileV2Props> = ({
  title,
  description,
  data,
  labelKey,
  valueKey,
  loading,
  na,
  emptyMessage,
  referenceLines,
  labelMaxChars,
  height = 280,
  color = "#4F46E5",
  colorRamp = false,
  showValueLabels = true,
  valueFormatter,
  horizontal = false,
}) => {
  const intl = useIntl();
  const maxLen = labelMaxChars ?? 14;
  const formatTick = React.useCallback(
    (value: string) => {
      const s = String(value ?? "");

      return s.length > maxLen ? `${s.slice(0, Math.max(1, maxLen - 1))}…` : s;
    },
    [maxLen],
  );

  const empty = !na && !loading && (!data || data.length === 0);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4}>
        <Box as="div" fontSize={2} color="default2">
          {title}
        </Box>
        {description && (
          <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
            {description}
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center" color="default2" style={{ height }}>
        {na ? (
          intl.formatMessage(analyticsMessages.commonNA)
        ) : loading ? (
          intl.formatMessage(analyticsMessages.commonLoading)
        ) : empty ? (
          <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} compact />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout={horizontal ? "vertical" : "horizontal"}
              margin={{ top: 16, right: 16, bottom: 4, left: 0 }}
            >
              <defs>
                <linearGradient id="barGradV2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={1} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15, 23, 42, 0.06)" vertical={horizontal} horizontal={!horizontal} />
              {horizontal ? (
                <>
                  <YAxis
                    type="category"
                    dataKey={labelKey}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                    tickLine={false}
                    axisLine={false}
                    width={120}
                    tickFormatter={formatTick}
                  />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={false} tickFormatter={v => (valueFormatter ? valueFormatter(v) : String(v))} />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={labelKey}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                    interval={0}
                    angle={0}
                    height={36}
                    tickFormatter={formatTick}
                    tickMargin={8}
                    minTickGap={6}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(15, 23, 42, 0.08)" }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={false} tickFormatter={v => (valueFormatter ? valueFormatter(v) : String(v))} width={48} />
                </>
              )}
              <Tooltip
                content={<CustomTooltip valueFormatter={valueFormatter} />}
                cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
              />
              {referenceLines?.map((ref, idx) => (
                <ReferenceLine
                  key={idx}
                  y={horizontal ? undefined : ref.y}
                  x={horizontal ? ref.y : undefined}
                  stroke={ref.color}
                  strokeDasharray={ref.strokeDasharray ?? "4 4"}
                  label={ref.label ? { value: ref.label, fill: ref.color, fontSize: 10, position: "right" } : undefined}
                />
              ))}
              <Bar dataKey={valueKey} radius={[6, 6, 0, 0]} fill="url(#barGradV2)">
                {colorRamp &&
                  data.map((_, i) => (
                    <Cell key={i} fill={seriesColors[i % seriesColors.length]} />
                  ))}
                {showValueLabels && (
                  <LabelList
                    dataKey={valueKey}
                    position={horizontal ? "right" : "top"}
                    style={{ fontSize: 11, fill: "#374151", fontWeight: 600 }}
                    formatter={(v: number) => (valueFormatter ? valueFormatter(v) : String(v))}
                  />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};
